import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, FolderPlus } from 'lucide-react';
import { Collection } from '../../stores';
import { useCollections } from '../../hooks/useCollections';

interface CollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingCollection?: Collection | null;
}

interface FormData {
  name: string;
  description: string;
}

const emptyFormData = (): FormData => ({
  name: '',
  description: '',
});

export function CollectionDialog({ open, onOpenChange, editingCollection }: CollectionDialogProps) {
  const { createCollection, updateCollection } = useCollections();
  const [formData, setFormData] = useState<FormData>(emptyFormData());

  // Populate form when editing
  useEffect(() => {
    if (open && editingCollection) {
      setFormData({
        name: editingCollection.name,
        description: editingCollection.description || '',
      });
    } else if (!open) {
      setFormData(emptyFormData());
    }
  }, [open, editingCollection]);

  const isEditing = !!editingCollection;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      return;
    }

    if (isEditing && editingCollection) {
      await updateCollection(editingCollection.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
      });
    } else {
      await createCollection(
        formData.name.trim(),
        formData.description.trim() || undefined,
      );
    }

    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.metaKey) {
      handleSubmit(e);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-lg shadow-xl z-50 w-[450px] max-h-[85vh] overflow-hidden"
          onKeyDown={handleKeyDown}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <FolderPlus className="w-5 h-5 text-yellow-500" />
              </div>
              <Dialog.Title className="text-lg font-semibold">
                {isEditing ? 'Edit Collection' : 'New Collection'}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button className="p-1 rounded hover:bg-accent transition-colors">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="space-y-2">
              <label htmlFor="collection-name" className="text-sm font-medium text-foreground">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="collection-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="My Collection"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="collection-description" className="text-sm font-medium text-foreground">
                Description
              </label>
              <textarea
                id="collection-description"
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description for this collection..."
                rows={3}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.name.trim()}
                className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isEditing ? 'Save Changes' : 'Create Collection'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
